import React, { useMemo } from "react";
import Field from "./shared/Field";

function normalizeCtaTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();
  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";
  return "template1";
}

function Group({ title, subtitle, defaultOpen = true, children, toggleLabel, t = (k, f) => f }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          {toggleLabel || t?.("ctaEditor.common.toggle", "Toggle")}
        </div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${active
              ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
              : "text-gray-600 hover:text-gray-900"
              }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function StringListEditor({ label, value, onChange, placeholder, t = (k, f) => f }) {
  const items = Array.isArray(value) ? value : [];

  const update = (idx, nextVal) => {
    const next = items.map((v, i) => (i === idx ? nextVal : v));
    onChange(next);
  };

  const add = () => onChange([...(items || []), ""]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-700">{label}</div>

      {items.length === 0 ? (
        <div className="text-xs text-gray-500">{t("ctaEditor.common.noItems", "No items yet.")}</div>
      ) : null}

      <div className="space-y-2">
        {items.map((v, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input
              value={v ?? ""}
              onChange={(e) => update(idx, e.target.value)}
              placeholder={placeholder || t("ctaEditor.template2.itemPlaceholder", "Item...")}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
            <button
              type="button"
              onClick={() => remove(idx)}
              className="px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
            >
              {t("ctaEditor.common.remove", "Remove")}
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        className="w-full py-2 rounded-xl border-2 border-dashed hover:bg-gray-50 text-sm font-medium"
      >
        {t("ctaEditor.common.addItem", "Add item")}
      </button>
    </div>
  );
}

export default function CTASectionEditor({ config, template, onChange, t = (k, f) => f }) {
  const cfg = config || {};
  const tplKey = useMemo(() => normalizeCtaTemplate(template), [template]);

  return (
    <div className="space-y-4">
      <Group
        t={t}
        title={t("ctaEditor.group.layout", "Layout")}
        subtitle={t("ctaEditor.group.layoutHelp", "Direction and main section heading")}
        toggleLabel={t("ctaEditor.group.layoutToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("ctaEditor.layoutDirection", "Layout Direction")}
            </div>

            <Segmented
              value={cfg.direction || "ltr"}
              onChange={(v) => onChange("direction", v)}
              options={[
                { value: "ltr", label: t("ctaEditor.direction.ltr", "LTR") },
                { value: "rtl", label: t("ctaEditor.direction.rtl", "RTL") },
              ]}
            />

            <div className="text-xs text-gray-500 mt-2">
              {t("ctaEditor.layoutDirectionHelp", "Use RTL for Arabic/Hebrew and LTR for English.")}
            </div>
          </div>

          <Field
            label={t("ctaEditor.header.fields.title", "Title")}
            value={cfg.title}
            onChange={(v) => onChange("title", v)}
          />

          <Field
            label={t("ctaEditor.header.fields.subtitle", "Subtitle")}
            type="textarea"
            value={cfg.subtitle}
            onChange={(v) => onChange("subtitle", v)}
          />
        </div>
      </Group>

      {tplKey === "template1" && (
        <>
          <Group
            t={t}
            title={t("ctaEditor.group.formFields", "WhatsApp Form")}
            subtitle={t("ctaEditor.group.formFieldsHelp", "Name, phone, message, and help text")}
            toggleLabel={t("ctaEditor.group.formFieldsToggle", "Toggle")}
            defaultOpen
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.template1.nameLabel", "Name Label")}
                value={cfg.nameLabel}
                onChange={(v) => onChange("nameLabel", v)}
              />
              <Field
                label={t("ctaEditor.template1.namePlaceholder", "Name Placeholder")}
                value={cfg.namePlaceholder}
                onChange={(v) => onChange("namePlaceholder", v)}
              />

              <Field
                label={t("ctaEditor.template1.phoneLabel", "Phone Label")}
                value={cfg.phoneLabel}
                onChange={(v) => onChange("phoneLabel", v)}
              />
              <Field
                label={t("ctaEditor.template1.phonePlaceholder", "Phone Placeholder")}
                value={cfg.phonePlaceholder}
                onChange={(v) => onChange("phonePlaceholder", v)}
              />

              <Field
                label={t("ctaEditor.template1.messageLabel", "Message Label")}
                value={cfg.messageLabel}
                onChange={(v) => onChange("messageLabel", v)}
              />
              <Field
                label={t("ctaEditor.template1.messagePlaceholder", "Message Placeholder")}
                value={cfg.messagePlaceholder}
                onChange={(v) => onChange("messagePlaceholder", v)}
              />

              <Field
                label={t("ctaEditor.template1.privacyText", "Bottom Help Text")}
                type="textarea"
                value={cfg.privacyText}
                onChange={(v) => onChange("privacyText", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.actions", "Actions & Success State")}
            subtitle={t("ctaEditor.group.actionsHelp", "Button and success message")}
            toggleLabel={t("ctaEditor.group.actionsToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.common.buttonText", "Button Text")}
                value={cfg.buttonText}
                onChange={(v) => onChange("buttonText", v)}
              />
              <Field
                label={t("ctaEditor.common.successTitle", "Success Title")}
                value={cfg.successTitle}
                onChange={(v) => onChange("successTitle", v)}
              />
              <Field
                label={t("ctaEditor.common.successSubtitle", "Success Subtitle")}
                value={cfg.successSubtitle}
                onChange={(v) => onChange("successSubtitle", v)}
              />
            </div>
          </Group>
        </>
      )}

      {tplKey === "template2" && (
        <>
          <Group
            t={t}
            title={t("ctaEditor.group.topBar", "Top Badge")}
            subtitle={t("ctaEditor.group.topBarHelp", "Small badge above the main CTA card")}
            toggleLabel={t("ctaEditor.group.topBarToggle", "Toggle")}
            defaultOpen
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.template2.badgeText", "Badge Text")}
                value={cfg.badgeText}
                onChange={(v) => onChange("badgeText", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.tabs", "Tabs")}
            subtitle={t("ctaEditor.group.tabsHelp", "WhatsApp and Call labels")}
            toggleLabel={t("ctaEditor.group.tabsToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label={t("ctaEditor.template2.tabWhatsApp", "WhatsApp Tab")}
                value={cfg.tabWhatsApp}
                onChange={(v) => onChange("tabWhatsApp", v)}
              />
              <Field
                label={t("ctaEditor.template2.tabWhatsAppHint", "WhatsApp Tab Hint")}
                value={cfg.tabWhatsAppHint}
                onChange={(v) => onChange("tabWhatsAppHint", v)}
              />
              <Field
                label={t("ctaEditor.template2.tabPhone", "Call Tab")}
                value={cfg.tabPhone}
                onChange={(v) => onChange("tabPhone", v)}
              />
              <Field
                label={t("ctaEditor.template2.tabPhoneHint", "Call Tab Hint")}
                value={cfg.tabPhoneHint}
                onChange={(v) => onChange("tabPhoneHint", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.whatsapp", "WhatsApp Channel")}
            subtitle={t("ctaEditor.group.whatsappHelp", "WhatsApp form and quick actions")}
            toggleLabel={t("ctaEditor.group.whatsappToggle", "Toggle")}
            defaultOpen
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.template2.whatsappFormTitle", "WhatsApp Form Title")}
                value={cfg.whatsappFormTitle}
                onChange={(v) => onChange("whatsappFormTitle", v)}
              />

              <Field
                label={t("ctaEditor.template2.namePlaceholder", "Name Placeholder")}
                value={cfg.namePlaceholder}
                onChange={(v) => onChange("namePlaceholder", v)}
              />

              <Field
                label={t("ctaEditor.template2.phonePlaceholder", "Phone Placeholder")}
                value={cfg.phonePlaceholder}
                onChange={(v) => onChange("phonePlaceholder", v)}
              />

              <Field
                label={t("ctaEditor.template2.messagePlaceholder", "Message Placeholder")}
                value={cfg.messagePlaceholder}
                onChange={(v) => onChange("messagePlaceholder", v)}
              />

              <Field
                label={t("ctaEditor.template2.whatsappCardTitle", "WhatsApp Card Title")}
                value={cfg.whatsappCardTitle}
                onChange={(v) => onChange("whatsappCardTitle", v)}
              />

              <Field
                label={t("ctaEditor.template2.whatsappCardSubtitle", "WhatsApp Card Subtitle")}
                value={cfg.whatsappCardSubtitle}
                onChange={(v) => onChange("whatsappCardSubtitle", v)}
              />

              <StringListEditor
                label={t("ctaEditor.template2.whatsappQuickTemplates", "Quick Message Templates")}
                value={cfg.whatsappTemplates}
                onChange={(next) => onChange("whatsappTemplates", next)}
                placeholder={t("ctaEditor.template2.itemPlaceholder", "Item...")}
                t={t}
              />

              <Field
                label={t("ctaEditor.template2.whatsappStartText", "Main WhatsApp Button")}
                value={cfg.whatsappStartText}
                onChange={(v) => onChange("whatsappStartText", v)}
              />

              <Field
                label={t("ctaEditor.template2.whatsappFooterLine", "WhatsApp Footer Text")}
                value={cfg.whatsappFooterLine}
                onChange={(v) => onChange("whatsappFooterLine", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.phone", "Call Channel")}
            subtitle={t("ctaEditor.group.phoneHelp", "Simple call block")}
            toggleLabel={t("ctaEditor.group.phoneToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.template2.phoneTitle", "Call Title")}
                value={cfg.phoneTitle}
                onChange={(v) => onChange("phoneTitle", v)}
              />
              <Field
                label={t("ctaEditor.template2.phoneHours", "Call Hours")}
                value={cfg.phoneHours}
                onChange={(v) => onChange("phoneHours", v)}
              />
              <Field
                label={t("ctaEditor.template2.phoneCallNowText", "Call Now Text")}
                value={cfg.phoneCallNowText}
                onChange={(v) => onChange("phoneCallNowText", v)}
              />
              <Field
                label={t("ctaEditor.template2.phoneRatesText", "Footer Note")}
                value={cfg.phoneRatesText}
                onChange={(v) => onChange("phoneRatesText", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.footerTrust", "Footer & Trust")}
            subtitle={t("ctaEditor.group.footerTrustHelp", "Footer stats and trust badges")}
            toggleLabel={t("ctaEditor.group.footerTrustToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <StringListEditor
                label={t("ctaEditor.template2.footerStats", "Footer Stats")}
                value={cfg.footerStats}
                onChange={(next) => onChange("footerStats", next)}
                placeholder={t("ctaEditor.template2.itemPlaceholder", "Item...")}
                t={t}
              />

              <StringListEditor
                label={t("ctaEditor.template2.trustBadges", "Trust Badges")}
                value={cfg.trustBadges}
                onChange={(next) => onChange("trustBadges", next)}
                placeholder={t("ctaEditor.template2.itemPlaceholder", "Item...")}
                t={t}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.actions", "Actions & Success State")}
            subtitle={t("ctaEditor.group.actionsHelp", "Button and success message")}
            toggleLabel={t("ctaEditor.group.actionsToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.common.buttonText", "Send Button Text")}
                value={cfg.buttonText}
                onChange={(v) => onChange("buttonText", v)}
              />
              <Field
                label={t("ctaEditor.common.successTitle", "Success Title")}
                value={cfg.successTitle}
                onChange={(v) => onChange("successTitle", v)}
              />
              <Field
                label={t("ctaEditor.common.successSubtitle", "Success Subtitle")}
                value={cfg.successSubtitle}
                onChange={(v) => onChange("successSubtitle", v)}
              />
            </div>
          </Group>
        </>
      )}

      {tplKey === "template3" && (
        <>
          <Group
            t={t}
            title={t("ctaEditor.group.contactCards", "Contact Cards")}
            subtitle={t("ctaEditor.group.contactCardsHelp", "WhatsApp, phone, and office card content")}
            toggleLabel={t("ctaEditor.group.contactCardsToggle", "Toggle")}
            defaultOpen
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.template3.whatsappCardTitle", "WhatsApp Card Title")}
                value={cfg.whatsappCardTitle}
                onChange={(v) => onChange("whatsappCardTitle", v)}
              />
              <Field
                label={t("ctaEditor.template3.whatsappCardValue", "WhatsApp Card Value")}
                value={cfg.whatsappCardValue}
                onChange={(v) => onChange("whatsappCardValue", v)}
              />
              <Field
                label={t("ctaEditor.template3.whatsappCardNote", "WhatsApp Card Note")}
                value={cfg.whatsappCardNote}
                onChange={(v) => onChange("whatsappCardNote", v)}
              />

              <Field
                label={t("ctaEditor.template3.phoneCardTitle", "Phone Card Title")}
                value={cfg.phoneCardTitle}
                onChange={(v) => onChange("phoneCardTitle", v)}
              />
              <Field
                label={t("ctaEditor.template3.phoneCardValue", "Phone Card Value")}
                value={cfg.phoneCardValue}
                onChange={(v) => onChange("phoneCardValue", v)}
              />
              <Field
                label={t("ctaEditor.template3.phoneCardNote", "Phone Card Note")}
                value={cfg.phoneCardNote}
                onChange={(v) => onChange("phoneCardNote", v)}
              />

              <Field
                label={t("ctaEditor.template3.officeCardTitle", "Office Card Title")}
                value={cfg.officeCardTitle}
                onChange={(v) => onChange("officeCardTitle", v)}
              />
              <Field
                label={t("ctaEditor.template3.officeCardValue", "Office Card Value")}
                type="textarea"
                value={cfg.officeCardValue}
                onChange={(v) => onChange("officeCardValue", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.tabsForm", "Tabs & WhatsApp Form")}
            subtitle={t("ctaEditor.group.tabsFormHelp", "Tab labels and WhatsApp form placeholders")}
            toggleLabel={t("ctaEditor.group.tabsFormToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.template3.tabWhatsAppLabel", "WhatsApp Tab Label")}
                value={cfg.tabWhatsAppLabel}
                onChange={(v) => onChange("tabWhatsAppLabel", v)}
              />
              <Field
                label={t("ctaEditor.template3.tabPhoneLabel", "Phone Tab Label")}
                value={cfg.tabPhoneLabel}
                onChange={(v) => onChange("tabPhoneLabel", v)}
              />

              <Field
                label={t("ctaEditor.template3.formNamePlaceholder", "Name Placeholder")}
                value={cfg.formNamePlaceholder}
                onChange={(v) => onChange("formNamePlaceholder", v)}
              />
              <Field
                label={t("ctaEditor.template3.formPhonePlaceholder", "Phone Placeholder")}
                value={cfg.formPhonePlaceholder}
                onChange={(v) => onChange("formPhonePlaceholder", v)}
              />
              <Field
                label={t("ctaEditor.template3.formMessagePlaceholder", "Message Placeholder")}
                value={cfg.formMessagePlaceholder}
                onChange={(v) => onChange("formMessagePlaceholder", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.phonePanel", "Phone Panel")}
            subtitle={t("ctaEditor.group.phonePanelHelp", "Phone call panel text")}
            toggleLabel={t("ctaEditor.group.phonePanelToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.template3.phonePanelTitle", "Phone Panel Title")}
                value={cfg.phonePanelTitle}
                onChange={(v) => onChange("phonePanelTitle", v)}
              />
              <Field
                label={t("ctaEditor.template3.phonePanelSubtitle", "Phone Panel Subtitle")}
                value={cfg.phonePanelSubtitle}
                onChange={(v) => onChange("phonePanelSubtitle", v)}
              />
              <Field
                label={t("ctaEditor.template3.phoneWaitText", "Phone Wait Text")}
                value={cfg.phoneWaitText}
                onChange={(v) => onChange("phoneWaitText", v)}
              />
              <Field
                label={t("ctaEditor.template3.phoneCallNowText", "Call Now Text")}
                value={cfg.phoneCallNowText}
                onChange={(v) => onChange("phoneCallNowText", v)}
              />
              <Field
                label={t("ctaEditor.template3.phoneIntlText", "Phone Note")}
                type="textarea"
                value={cfg.phoneIntlText}
                onChange={(v) => onChange("phoneIntlText", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.metrics", "Metrics")}
            subtitle={t("ctaEditor.group.metricsHelp", "Small summary values shown in the lower area")}
            toggleLabel={t("ctaEditor.group.metricsToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.template3.metric1Label", "Metric 1 Label")}
                value={cfg.metric1Label}
                onChange={(v) => onChange("metric1Label", v)}
              />
              <Field
                label={t("ctaEditor.template3.metric1Value", "Metric 1 Value")}
                value={cfg.metric1Value}
                onChange={(v) => onChange("metric1Value", v)}
              />
              <Field
                label={t("ctaEditor.template3.metric2Label", "Metric 2 Label")}
                value={cfg.metric2Label}
                onChange={(v) => onChange("metric2Label", v)}
              />
              <Field
                label={t("ctaEditor.template3.metric2Value", "Metric 2 Value")}
                value={cfg.metric2Value}
                onChange={(v) => onChange("metric2Value", v)}
              />
            </div>
          </Group>

          <Group
            t={t}
            title={t("ctaEditor.group.actions", "Actions & Success State")}
            subtitle={t("ctaEditor.group.actionsHelp", "Button and success message")}
            toggleLabel={t("ctaEditor.group.actionsToggle", "Toggle")}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <Field
                label={t("ctaEditor.common.buttonText", "Send Button Text")}
                value={cfg.buttonText}
                onChange={(v) => onChange("buttonText", v)}
              />
              <Field
                label={t("ctaEditor.common.successTitle", "Success Title")}
                value={cfg.successTitle}
                onChange={(v) => onChange("successTitle", v)}
              />
              <Field
                label={t("ctaEditor.common.successSubtitle", "Success Subtitle")}
                value={cfg.successSubtitle}
                onChange={(v) => onChange("successSubtitle", v)}
              />
            </div>
          </Group>
        </>
      )}
    </div>
  );
}